/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Tooltip, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { useNavigate } from "react-router-dom";
import { showErrorMessage } from "../../../../utils";

const DeliveryMISReportNew = () => {
    const FILTER_STORAGE_KEY = "DELIVERY_MIS_REPORT_NEW_FILTERS";
    const { createNewRequest, isLatestRequest } = useRequestCancellation();
    const filtersRestoredRef = useRef(false);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment(),
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedCps, setSelectedCps] = useState([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [pendingCpSelection, setPendingCpSelection] = useState(null);

    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange
                ? [
                    dateRange[0]?.format("YYYY-MM-DD"),
                    dateRange[1]?.format("YYYY-MM-DD"),
                ]
                : null,
            selectedHubs,
            selectedCps,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize,
            },
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                // Check if current user is hub-restricted — their hub must always come
                // from their login credentials, never from stale session storage.
                const currentUser = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const role = currentUser?.roles?.[0];
                const isHubRestricted =
                    role === "ROLE_HUBADMIN" || role === "ROLE_HUBOPERATOR";

                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1]),
                    ]);
                }
                // Skip restoring hub for restricted roles — will be set by hubList effect
                if (!isHubRestricted && filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedCps && filters.selectedCps.length > 0) {
                    setPendingCpSelection(filters.selectedCps);
                }
                if (filters.pagination) {
                    setPagination((prev) => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize,
                    }));
                }
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return !isHubRestricted; // treat as "not restored" for hub-restricted users
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    const keyMapping = {
        Outscanned: "outscanned",
        "Inscanned (Same Day)": "inscanned",
        "Not Inscanned (Same Day)": "notInScan",
        "OFD (Same Day)": "drsGenerationByCPOfSameDay",
        "Delivered (Same Day)": "delivered",
        "Undelivered (Same Day)": "undelivered",
        "RTO Initiated (Same Day)": "rto",
        "RTO OFD (Same Day)": "rtoOfd",
        "RTO Delivered (Same Day)": "rtoDelivered",
        "RTO Undelivered (Same Day)": "rtoUndelivered",
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                const restored = restoreFiltersFromStorage();
                filtersRestoredRef.current = restored;
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!selectedHubs || selectedHubs.length === 0) {
            setCpList([]);
            setSelectedCps([]);
            setPendingCpSelection(null);
            return;
        }

        (async () => {
            try {
                const cpLists = await Promise.all(
                    selectedHubs.map((hubId) => getHubCPList(hubId)),
                );
                const allCps = [...new Set(cpLists.flat())];
                setCpList(allCps);
            } catch (error) {
                message.error("Failed to fetch CP list");
                setCpList([]);
            }
        })();
    }, [selectedHubs]);

    useEffect(() => {
        if (pendingCpSelection && cpList.length > 0) {
            const validCpIds = pendingCpSelection.filter((cpId) =>
                cpList.some((cp) => cp.value === cpId),
            );
            if (validCpIds.length > 0) {
                setSelectedCps(validCpIds);
            }
            setPendingCpSelection(null);
        }
    }, [cpList, pendingCpSelection]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        setHubUser(user?.roles?.[0]);

        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;
        const role = user?.roles?.[0];
        const isHubRestricted =
            role === "ROLE_HUBADMIN" || role === "ROLE_HUBOPERATOR";

        if (hubList.length > 0) {
            if (isHubRestricted) {
                // Always enforce the logged-in user's own hub — ignore any saved/stale hub
                const assignedHubId = activeHubId || userid?.userInfo?.premiseId;
                const assignedHub = hubList.find(
                    (hub) => String(hub.value) === String(assignedHubId),
                );
                if (assignedHub) {
                    setSelectedHubs([assignedHub.value]);
                }
            } else if (selectedHubs.length === 0 && !filtersRestoredRef.current) {
                // Non-restricted users: use active_hub preference or default to first hub
                if (activeHubId) {
                    const defaultHub = hubList.find(
                        (hub) => String(hub.value) === String(activeHubId),
                    );
                    if (defaultHub) {
                        setSelectedHubs([defaultHub.value]);
                    }
                } else {
                    setSelectedHubs([hubList[0].value]);
                }
            }
        }
    }, [hubList]);

    const calendarRef = useRef();

    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            setDateRange([moment(), moment()]);
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    useEffect(() => {
        if (!hubUser) return;
        if (
            (hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") &&
            selectedHubs.length === 0
        )
            return;
        const hasValidDates =
            Array.isArray(dateRange) &&
            dateRange[0] &&
            dateRange[1] &&
            typeof dateRange[0].format === "function" &&
            typeof dateRange[1].format === "function";
        if (!hasValidDates) return;
        if (
            selectedCps &&
            selectedCps.length > 0 &&
            cpList.length === 0 &&
            selectedHubs.length > 0
        )
            return;
        if (pendingCpSelection) return;
        fetchReport();
        // eslint-disable-next-line
    }, [
        hubUser,
        dateRange,
        pagination.current,
        pagination.pageSize,
        selectedHubs,
        selectedCps,
        pendingCpSelection,
    ]);

    const fetchReport = async () => {
        const requestId = createNewRequest();
        setLoading(true);
        let shouldUpdateState = true;
        try {
            const hasValidDates =
                Array.isArray(dateRange) &&
                dateRange[0] &&
                dateRange[1] &&
                typeof dateRange[0].format === "function" &&
                typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            if (isLatestRequest(requestId.requestId)) {
                setReportData([]);
            }

            const queryParams = new URLSearchParams({
                fromDate_date: dateRange[0].format("YYYY-MM-DD"),
                toDate_date: dateRange[1].format("YYYY-MM-DD"),
                page: pagination.current,
                size: pagination.pageSize,
            });

            if (selectedHubs && selectedHubs.length > 0) {
                queryParams.append("premise_id", selectedHubs.join(","));
            }
            if (selectedCps && selectedCps.length > 0) {
                const selectedCpLabels = cpList
                    .filter((cp) => selectedCps.includes(cp.value))
                    .map((cp) => cp.label.toUpperCase());
                if (selectedCpLabels.length > 0) {
                    queryParams.append("channel_partner", selectedCpLabels.join(","));
                }
            }

            const url = `https://a3-framework.prayog.io/dynamic/t3_delivery_mis_report_de_d0f567?${queryParams.toString()}`;

            const body = {
                user_name: "laxminandan",
                user_email: "laxminandan.handique@shreemaruti.com",
            };

            const [fetchResponse, scheduleResponse] = await Promise.all([
                fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization:
                            "Bearer 542735477f9c35f653ef6b3bf9d9cbbf577f8f0a75c45f4221a7590de8ae4f90",
                    },
                    body: JSON.stringify(body),
                }),
                ReportsService.getDeliveryReportsRefreshSchedule({ signal: requestId.signal })
            ]);

            // Extract and set refresh schedule data
            setLastsynced(scheduleResponse?.lastRefreshedAt || null);
            setNextScheduledRefresh(scheduleResponse?.nextScheduledRefresh || null);

            if (!fetchResponse.ok) {
                const errorData = await fetchResponse.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to fetch report data");
            }

            const responseData = await fetchResponse.json();
            const response = {
                response: {
                    data: responseData,
                    status: fetchResponse.status,
                    ok: fetchResponse.ok,
                },
            };

            if (!isLatestRequest(requestId.requestId)) {
                shouldUpdateState = false;
            } else {
                if (response?.response?.status === 200) {
                    const data = response?.response?.data?.data || [];
                    const count =
                        response?.response?.data?.total_records ||
                        (Array.isArray(data) ? data.length : 0);

                    const mappedData = data.map((item) => ({
                        ...item,
                        hubId: item.hub_id || item.hubId || item.from_hub_id,
                        cpId: item.cp_id || item.cpId || item.to_cp_id,
                        hub: item.hub || item.hubName || item.hub_name,
                        channel_partner:
                            item.channel_partner || item.cpName || item.cp_name || item.cp,
                        shipments_outscanned:
                            item.shipments_outscanned ||
                            item.outscanByHub ||
                            item.outscan_by_hub,
                        shipments_not_inscanned_on_same_day:
                            item.shipments_not_inscanned_on_same_day ||
                            item.notInScanByCP ||
                            item.not_in_scan_by_cp ||
                            item.not_inscan_by_cp,
                        shipments_inscanned_on_same_day:
                            item.shipments_inscanned_on_same_day ||
                            item.inscanByCP ||
                            item.inscan_by_cp,
                        shipments_ofd_on_same_day:
                            item.shipments_ofd_on_same_day ||
                            item.drsGenerationByCPOfSameDay ||
                            item.drs_generation_by_cp_of_same_day ||
                            item.drs_generation_by_cp_same_day,
                        shipments_delivered_on_same_day:
                            item.shipments_delivered_on_same_day ||
                            item.sameDayDeliveries ||
                            item.same_day_deliveries,
                        shipments_undelivered_on_same_day:
                            item.shipments_undelivered_on_same_day ||
                            item.undelivered ||
                            item.un_delivered,
                        shipments_rto_initiated_on_same_day:
                            item.shipments_rto_initiated_on_same_day || item.rto,
                    }));

                    setReportData(mappedData);
                    setPagination((prev) => ({
                        ...prev,
                        total: count,
                    }));
                } else {
                    throw new Error(response?.message || "Failed to fetch report data");
                }
            }
        } catch (error) {
            console.error("fetchReport error:", error);
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                showErrorMessage("Failed to fetch report data");
                setReportData([]);
                setPagination((prev) => ({
                    ...prev,
                    total: 0,
                }));
            }
        } finally {
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }


        setDownloadLoading(true);
        try {
            const queryParams = new URLSearchParams({
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
            });

            if (selectedHubs && selectedHubs.length > 0) {
                queryParams.append("premiseId", selectedHubs.join(","));
            }

            if (selectedCps && selectedCps.length > 0) {
                const selectedCpLabels = cpList
                    .filter((cp) => selectedCps.includes(cp.value))
                    .map((cp) => cp.label.toUpperCase());
                if (selectedCpLabels.length > 0) {
                    queryParams.append("channelPartner", selectedCpLabels.join(","));
                }
            }

            const url = `https://apis-hubops.innofulfill.com/report-service/api/v1/dynamic-report/delivery-mis-report/download?${queryParams.toString()}`;

            const userInfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
            const token = userInfo?.token;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;

                const contentDisposition = response.headers.get("content-disposition");
                let fileName = `Delivery_MIS_Report_${moment().format(
                    "YYYYMMDD_HHmmss",
                )}.csv`;
                if (contentDisposition) {
                    const fileNameMatch =
                        contentDisposition.match(/filename="?([^"]+)"?/);
                    if (fileNameMatch && fileNameMatch.length === 2) {
                        fileName = fileNameMatch[1];
                    }
                }

                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
                message.success("Delivery MIS Report downloaded successfully");
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to download report");
            }
        } catch (error) {
            console.error("Download error:", error);
            message.error(error?.message || "Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleNumberClick = (type, value, record) => {
        if (!value) return;

        saveFiltersToStorage();

        const key = keyMapping[type];
        if (!key) {
            return;
        }

        navigate("/delivery-details", {
            state: {
                date: record.date || dateRange[0].format("YYYY-MM-DD"),
                fromDate: record.date || dateRange[0].format("YYYY-MM-DD"),
                toDate: record.date || dateRange[0].format("YYYY-MM-DD"),
                cpId: record?.cpId || record?.cp_id || record?.to_cp_id,
                hubId: record?.premise_id || record?.premise_id || record?.premise_id,
                key: key,
                type: type,
                hubName: record?.hub || record?.hubName || record?.hub_name || record?.from_hub,
                cpName: record?.channel_partner || record?.cpName || record?.cp_name || record?.to_channel_partner,
                isNewReport: true,
            },
        });
    };

    const renderClickableNumber = (value, type, record) => {
        if (!value && value !== 0) return "---";
        return (
            <Tooltip title={`Click to view ${type} details`}>
                <a
                    onClick={() => handleNumberClick(type, value, record)}
                    style={{
                        color: "#1890ff",
                        cursor: "pointer",
                        display: "block",
                        textAlign: "center",
                    }}
                >
                    {Number(value).toLocaleString()}
                </a>
            </Tooltip>
        );
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            align: "center",
            render: (_, __, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: true,
            width: 100,
            align: "center",
            render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "---"),
        },
        {
            title: "Hub",
            dataIndex: "hub",
            key: "hub",
            sorter: true,
            width: 160,
            align: "center",
            render: (text) => text || "---",
        },
        {
            title: "Channel Partner",
            dataIndex: "channel_partner",
            key: "channel_partner",
            sorter: true,
            width: 160,
            align: "center",
            render: (text) => text || "---",
        },
        {
            title: "Outscanned",
            dataIndex: "shipments_outscanned",
            key: "shipments_outscanned",
            sorter: true,
            width: 130,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "Outscanned", record),
        },
        {
            title: "Inscanned (Same Day)",
            dataIndex: "shipments_inscanned_on_same_day",
            key: "shipments_inscanned_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "Inscanned (Same Day)", record),
        },
        {
            title: "Not Inscanned (Same Day)",
            dataIndex: "shipments_not_inscanned_on_same_day",
            key: "shipments_not_inscanned_on_same_day",
            sorter: true,
            width: 200,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "Not Inscanned (Same Day)", record),
        },
        {
            title: "OFD (Same Day)",
            dataIndex: "shipments_ofd_on_same_day",
            key: "shipments_ofd_on_same_day",
            sorter: true,
            width: 150,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "OFD (Same Day)", record),
        },
        {
            title: "Delivered (Same Day)",
            dataIndex: "shipments_delivered_on_same_day",
            key: "shipments_delivered_on_same_day",
            sorter: true,
            width: 170,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "Delivered (Same Day)", record),
        },
        {
            title: "Undelivered (Same Day)",
            dataIndex: "shipments_undelivered_on_same_day",
            key: "shipments_undelivered_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "Undelivered (Same Day)", record),
        },
        {
            title: "RTO Initiated (Same Day)",
            dataIndex: "shipments_rto_initiated_on_same_day",
            key: "shipments_rto_initiated_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "RTO Initiated (Same Day)", record),
        },
        {
            title: "RTO OFD (Same Day)",
            dataIndex: "shipments_rto_ofd_on_same_day",
            key: "shipments_rto_ofd_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "RTO OFD (Same Day)", record),
        },
        {
            title: "RTO Delivered (Same Day)",
            dataIndex: "shipments_rto_delivered_on_same_day",
            key: "shipments_rto_delivered_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "RTO Delivered (Same Day)", record),
        },
        {
            title: "RTO Undelivered (Same Day)",
            dataIndex: "shipments_rto_undelivered_on_same_day",
            key: "shipments_rto_undelivered_on_same_day",
            sorter: true,
            width: 180,
            align: "center",
            render: (value, record) =>
                renderClickableNumber(value, "RTO Undelivered (Same Day)", record),
        },
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Delivery MIS Report (NEW)">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Hub"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedHubs(value);
                            }}
                            value={selectedHubs}
                            allowClear
                            mode={
                                hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR"
                                    ? "multiple"
                                    : "default"
                            }
                            disabled={
                                hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"
                            }
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">CP</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="CP"
                            options={cpList}
                            onChange={(value) => {
                                setSelectedCps(value);
                            }}
                            value={selectedCps}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                        lg={6}
                        style={{ textAlign: "right", marginTop: 24 }}
                    >
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={
                                !dateRange ||
                                !dateRange[0] ||
                                !dateRange[1]
                            }
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
            />
        </div>
    );
};

export default DeliveryMISReportNew;
